import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveComWixpressPortfolioPortfolioappPortfolioSettingsServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'manage._base_domain_': [
      {
        srcPath: '/_api/portfolio-app-service',
        destPath: '/api',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/portfolio-app-service',
        destPath: '/api',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/portfolio-app-service',
        destPath: '/api',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/portfolio-app-service',
        destPath: '/api',
      },
    ],
    _: [
      {
        srcPath: '/_api/portfolio-app-service',
        destPath: '/api',
      },
    ],
    'api._api_base_domain_': [
      {
        srcPath: '/_api/portfolio-app-service',
        destPath: '/api',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/portfolio/v1/settings',
        destPath: '/api/v1/portfolio/settings',
      },
      {
        srcPath: '/portfolio/portfolio-app/api/v1/portfolio/settings',
        destPath: '/api/v1/portfolio/settings',
      },
      {
        srcPath:
          '/portfolio/project-items/api/v1/portfolio/project/items/media',
        destPath: '/api/v1/portfolio/project/items/media',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/portfolio/portfolio-app/api/v1/portfolio/settings',
        destPath: '/api/v1/portfolio/settings',
      },
      {
        srcPath:
          '/portfolio/project-items/api/v1/portfolio/project/items/media',
        destPath: '/api/v1/portfolio/project/items/media',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_portfolio_portfolio-settings';

/**
 * Creates a site's portfolio settings.
 *
 * > **Note:**
 * >
 * > This method is intended for creating portfolio settings. If settings already exist, the request will return an error. To update existing settings, call [Update Portfolio Settings](https://dev.wix.com/docs/rest/business-solutions/portfolio/portfolio-settings/update-portfolio-settings).
 */
export function createPortfolioSettings(
  payload: object
): RequestOptionsFactory<any> {
  function __createPortfolioSettings({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'portfolioSettings.createdDate' },
          { path: 'portfolioSettings.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.portfolio.portfolio_app.v1.portfolio_settings',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.portfolio.portfolioapp.PortfolioSettingsService.CreatePortfolioSettings',
      packageName: PACKAGE_NAME,
      url: resolveComWixpressPortfolioPortfolioappPortfolioSettingsServiceUrl({
        protoPath: '/api/v1/portfolio/settings',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'portfolioSettings.createdDate' },
              { path: 'portfolioSettings.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createPortfolioSettings;
}

/** Retrieves a site's portfolio settings. */
export function getPortfolioSettings(
  payload: object
): RequestOptionsFactory<any> {
  function __getPortfolioSettings({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.portfolio.portfolio_app.v1.portfolio_settings',
      method: 'GET' as any,
      methodFqn:
        'com.wixpress.portfolio.portfolioapp.PortfolioSettingsService.GetPortfolioSettings',
      packageName: PACKAGE_NAME,
      url: resolveComWixpressPortfolioPortfolioappPortfolioSettingsServiceUrl({
        protoPath: '/api/v1/portfolio/settings',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'portfolioSettings.createdDate' },
              { path: 'portfolioSettings.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getPortfolioSettings;
}

/** Updates a site's portfolio settings. */
export function updatePortfolioSettings(
  payload: object
): RequestOptionsFactory<any> {
  function __updatePortfolioSettings({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'mask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'portfolioSettings.createdDate' },
          { path: 'portfolioSettings.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.portfolio.portfolio_app.v1.portfolio_settings',
      method: 'PATCH' as any,
      methodFqn:
        'com.wixpress.portfolio.portfolioapp.PortfolioSettingsService.UpdatePortfolioSettings',
      packageName: PACKAGE_NAME,
      url: resolveComWixpressPortfolioPortfolioappPortfolioSettingsServiceUrl({
        protoPath: '/api/v1/portfolio/settings',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'updatedPortfolioSettings.createdDate' },
              { path: 'updatedPortfolioSettings.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updatePortfolioSettings;
}
